// Copyright ©️ 2026 eVolve MEP, LLC

import { useState } from 'react';

import { type PartCatalog, type PartCategory, useEvolvePut, useEvolveDelete } from '@evolvemep/api-ts';
import { ActionIcon, Stack, TextInput, Button, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { ExistingCategorySelector } from 'modules/Shop/CatalogSetup/ExistingCategorySelector';
import type { CategoryPath } from 'modules/Shop/CatalogSetup/useCategoryPath';

type Props = {
  partCatalog: PartCatalog;
  partCategory: PartCategory;
  parentPartCategory: CategoryPath[number] | null;
  onPartCategoryUpdated: (updatedPartCategory: PartCategory | null) => void;
};

export const EditCategoryModal = ({
  partCatalog,
  partCategory,
  parentPartCategory: initialParentPartCategory,
  onPartCategoryUpdated,
}: Props) => {
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [categoryName, setCategoryName] = useState(partCategory.name);
  const [parentPartCategory, setParentPartCategory] = useState(initialParentPartCategory);

  const { apiCall: deleteCategoryApiCall, loading: deletingCategory } = useEvolveDelete(
    `shop/partCatalog/category/${partCategory.partCategoryId}`,
  );

  const { apiCall: updateCategoryApiCall, loading: saving } = useEvolvePut(
    `shop/partCatalog/category/${partCategory.partCategoryId}`,
  );

  if (!partCategory.editable) return null;

  const onSubmit = async () => {
    await updateCategoryApiCall({
      ...partCategory,
      name: categoryName,
      parentPartCategoryId: parentPartCategory?.partCategoryId,
    }).then(onPartCategoryUpdated);
  };

  return (
    <>
      <WrappedModal
        title="Edit category"
        opened={editing}
        onClose={() => {
          setEditing(false);
          setCategoryName(partCategory.name);
          setParentPartCategory(initialParentPartCategory);
        }}
        onConfirm={onSubmit}
        loading={saving}
        disabled={
          !categoryName ||
          (categoryName.trim() === partCategory.name.trim() &&
            parentPartCategory?.partCategoryId === partCategory.parentPartCategoryId)
        }
        leftComponent={
          <Button
            variant="outline"
            color="red"
            onClick={() => {
              setEditing(false);
              setDeleting(true);
            }}
          >
            Delete
          </Button>
        }
      >
        <Stack>
          <TextInput
            label="Category Name"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
            disabled={saving}
            required
            autoFocus
            data-autofocus
            maxLength={64}
          />
          <ExistingCategorySelector
            label="Parent Category"
            partCatalog={partCatalog}
            onChange={setParentPartCategory}
            value={parentPartCategory}
            disabled={saving}
            placeholder="No parent category"
            clearable
            disableCategories={[partCategory.partCategoryId]}
          />
        </Stack>
      </WrappedModal>

      <WrappedModal
        title="Delete category"
        opened={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={() => deleteCategoryApiCall().then(() => onPartCategoryUpdated(null))}
        loading={deletingCategory}
        confirmationText="Delete"
        alertState={{
          title: 'Cannot delete category',
          showAlert: partCategory.partCount > 0,
          message: (
            <Text>This category contains parts. All parts must be removed from the category before deleting.</Text>
          ),
        }}
      >
        <Text>
          Are you sure you want to delete <b>{partCategory.name}</b>?
        </Text>
      </WrappedModal>

      <AgGridStyleTooltip label="Edit category" withArrow openDelay={500}>
        <ActionIcon
          size="xs"
          onClick={(e) => {
            setEditing(true);
            e.stopPropagation();
          }}
          color="dark"
          c="gray.8"
        >
          <EvolveIcon icon="Edit" size="sm" />
        </ActionIcon>
      </AgGridStyleTooltip>
    </>
  );
};
